import {
  createContext,
  FC,
  PropsWithChildren,
  TransitionStartFunction,
  useContext,
  useEffect,
  useTransition,
} from "react";
import {
  graphql,
  PreloadedQuery,
  usePreloadedQuery,
  useQueryLoader,
} from "react-relay";
import { isThor } from "rujira.js";
import { Id } from "../node";
import { useTradeOrderSubscriptions } from "../trade/TradeSubscriptions";
import {
  accountDataQuery,
  accountDataQuery$data,
} from "./__generated__/accountDataQuery.graphql";
import { useAccounts } from "./accounts";

const one = graphql`
  query accountDataQuery($id: ID, $addresses: [Address!]!) {
    node(id: $id) {
      ... on Account {
        ...BalanceContextFragment
        ...BalancesPoolFragment
        ...BorrowAccountFragment
        ...GhostVaultAccountFragment
        ...IndexAccountFragment
        ...IndexBalancesFragment
        ...MergingFragment
        ...OrderBookAccountFragment
        ...PortfolioFragment
        ...PositionsPortfolioFragment
        ...StrategyAccountFragment
        ...ThorchainPoolAccountFragment
        ...TradeSubscriptionsFragment
        ...useAutoClaimerFragment
      }
    }
  }
`;

const Context = createContext<{
  accountQuery: PreloadedQuery<accountDataQuery>;
  isLoading: boolean;
  transition: TransitionStartFunction;
}>({
  accountQuery: undefined as unknown as PreloadedQuery<accountDataQuery>,
  isLoading: false,
  transition: () => {},
});

export const AccountDataContext: FC<PropsWithChildren> = ({ children }) => {
  const { selected, accounts } = useAccounts();
  const addresses =
    accounts?.map((x) => x.address)?.filter((a) => !isThor(a)) || [];
  const [accountQuery, loadAccountData] = useQueryLoader<accountDataQuery>(one);
  const [isLoading, transition] = useTransition();

  useEffect(() => {
    const vars = selected
      ? { id: Id.account(selected), addresses }
      : { addresses };

    loadAccountData(vars, { fetchPolicy: "store-and-network" });
  }, [selected]);

  return (
    <Context.Provider
      value={{
        accountQuery: accountQuery as PreloadedQuery<accountDataQuery>,
        isLoading,
        transition,
      }}>
      {children}
    </Context.Provider>
  );
};

export const usePreloadedAccountData = (): {
  accountData?: accountDataQuery$data["node"];
  isLoading: boolean;
} => {
  const { accountQuery, isLoading } = useContext(Context);
  if (!accountQuery) {
    return { accountData: undefined, isLoading: false };
  }
  const query = usePreloadedQuery(one, accountQuery);
  useTradeOrderSubscriptions(query.node || undefined);
  return { accountData: query.node || undefined, isLoading };
};
